import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Printer, CheckCircle, Ban, Search, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import Pagination from '../../components/Pagination';
import { formatCATDate } from '../../utils/timezone';

export default function ClaimsHistoryTable({
  role, isSuperAdmin, hospitalName,
  claims, claimsTotal, claimsTotalPages,
  claimStatusFilter, onStatusFilterChange,
  claimSearch = '', onSearchChange,
  claimPageSize = 20, onPageSizeChange,
  claimPage, setClaimPage,
  hospitalCurrency, hospitalStats,
  onPrintClaim, onMarkAsPaid, onVoidClaim,
}) {
  return (
    <Card className="border-purple-200 shadow-md">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 border-b border-purple-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
          <CardTitle className="flex items-center gap-2 text-purple-900 text-sm sm:text-base">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
            {isSuperAdmin
              ? 'All Claims'
              : (role === 'Finance' || role === 'Reception' || role === 'DataEntry')
                ? `Claims - ${hospitalName}`
                : `Recent Claims`
            }
          </CardTitle>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="relative sm:w-64">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input value={claimSearch} onChange={(e) => onSearchChange?.(e.target.value)} placeholder="Search patient name, serial or claim ID" className="pl-8 pr-8 h-8 sm:h-10 text-sm bg-white" data-testid="claims-search" />
              {claimSearch && (
                <button type="button" onClick={() => onSearchChange?.('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Clear search" data-testid="claims-search-clear"><X className="w-4 h-4" /></button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs sm:text-sm text-purple-800">Filter:</Label>
              <Select value={claimStatusFilter} onValueChange={onStatusFilterChange}>
                <SelectTrigger className="w-[120px] sm:w-[130px] h-8 sm:h-10 text-sm" data-testid="claims-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                  <SelectItem value="VOIDED">Voided</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(claimPageSize)} onValueChange={(v) => onPageSizeChange?.(Number(v))}>
                <SelectTrigger className="w-[88px] h-8 sm:h-10 text-sm" data-testid="claims-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50].map((n) => <SelectItem key={n} value={String(n)}>{n} rows</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-2 sm:pt-6 sm:p-6">
        {claims.length === 0 ? (
          <p className="text-center text-gray-500 py-8" data-testid="claims-empty">{claimSearch ? `No claims match "${claimSearch}"` : claimStatusFilter !== 'ALL' ? `No claims found with status: ${claimStatusFilter}` : 'No claims created yet'}</p>
        ) : (
          <>
            {(claimStatusFilter !== 'ALL' || claimSearch) && (
              <div className="mb-4 text-xs sm:text-sm text-gray-600" data-testid="claims-result-summary">
                Showing <strong>{claims.length}</strong> on this page of <strong>{claimsTotal.toLocaleString()}</strong> {claimStatusFilter !== 'ALL' ? `${claimStatusFilter.toLowerCase()} ` : ''}claims{claimSearch ? <> matching <strong>"{claimSearch}"</strong></> : ''}
              </div>
            )}
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-xs sm:text-sm" data-testid="claims-table">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="text-left p-2 sm:p-3 font-semibold text-gray-700">Claim ID</th>
                    {isSuperAdmin && (
                      <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 hidden sm:table-cell">Hospital</th>
                    )}
                    <th className="text-left p-2 sm:p-3 font-semibold text-gray-700">Patient</th>
                    <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 hidden sm:table-cell">Date</th>
                    <th className="text-right p-2 sm:p-3 font-semibold text-gray-700">Amount</th>
                    <th className="text-center p-2 sm:p-3 font-semibold text-gray-700">Status</th>
                    <th className="text-center p-2 sm:p-3 font-semibold text-gray-700 hidden sm:table-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((claim) => {
                    let claimCurrency = hospitalCurrency;
                    let claimRate = hospitalCurrency.rate_to_usd || 1;

                    if (isSuperAdmin && claim.hospital_name && hospitalStats[claim.hospital_name]) {
                      const stats = hospitalStats[claim.hospital_name];
                      claimCurrency = {
                        symbol: stats.currency_symbol || '$',
                        decimal_places: stats.currency_decimals !== undefined ? stats.currency_decimals : 2,
                      };
                      claimRate = stats.rate_to_usd || 1;
                    }

                    const formatClaimAmount = (amountUSD) => {
                      const amountInLocalCurrency = amountUSD * claimRate;
                      const formattedAmount = amountInLocalCurrency.toLocaleString('en-US', {
                        minimumFractionDigits: claimCurrency.decimal_places,
                        maximumFractionDigits: claimCurrency.decimal_places,
                      });
                      return `${claimCurrency.symbol} ${formattedAmount}`;
                    };

                    return (
                      <tr key={claim.claim_id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => onPrintClaim(claim.claim_id)}>
                        <td className="p-2 sm:p-3 font-medium text-gray-800">{claim.claim_id}</td>
                        {isSuperAdmin && (
                          <td className="p-2 sm:p-3 text-gray-700 hidden sm:table-cell">
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                              {claim.hospital_name}
                            </span>
                          </td>
                        )}
                        <td className="p-2 sm:p-3 text-gray-700">{claim.patient_name}</td>
                        <td className="p-2 sm:p-3 text-gray-600 hidden sm:table-cell">{formatCATDate(claim.timestamp)}</td>
                        <td className="p-2 sm:p-3 text-gray-800 text-right font-medium">{formatClaimAmount(claim.total_claim_amount)}</td>
                        <td className="p-2 sm:p-3 text-center">
                          <span className={`inline-flex px-2 sm:px-3 py-1 text-xs font-semibold rounded-full ${
                            claim.status === 'PENDING'
                              ? 'bg-yellow-100 text-yellow-800'
                              : claim.status === 'PAID'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {claim.status}
                          </span>
                        </td>
                        <td className="p-2 sm:p-3 text-center hidden sm:table-cell">
                          <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              data-testid={`print-claim-${claim.claim_id}`}
                              onClick={() => onPrintClaim(claim.claim_id)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                            {claim.status === 'PENDING' && isSuperAdmin && (
                              <>
                                <Button
                                  data-testid={`pay-claim-${claim.claim_id}`}
                                  onClick={() => onMarkAsPaid(claim.claim_id, claim.total_claim_amount)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  title="Mark as Paid"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`void-claim-${claim.claim_id}`}
                                  onClick={() => onVoidClaim(claim.claim_id)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {claimsTotalPages > 1 && (
              <Pagination
                currentPage={claimPage}
                totalPages={claimsTotalPages}
                onPageChange={setClaimPage}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
